import type { AgentRunSnapshot } from '@corner/shared';
import type { CoTStepData } from '../components/app/cot/types';

export type V2CotStatus = 'planning' | 'working' | 'done' | 'error';

const TASK_STATUS_MAP: Record<string, CoTStepData['status']> = {
  pending:       'pending',
  running:       'active',
  completed:     'done',
  failed:        'error',
  skipped:       'skipped',
  replanning:    'active',
  retry_pending: 'active',
};

/** Merge agent snapshot tasks with any staged edit batches already on CoT steps (by task id or index). */
export function buildMergedV2CotSteps(snap: AgentRunSnapshot, prevSteps: CoTStepData[]): CoTStepData[] {
  const prevByTaskId = new Map(
    prevSteps.filter((s) => s.agentTaskId).map((s) => [s.agentTaskId!, s]),
  );
  return snap.tasks.map((t, i) => {
    const fromId = prevByTaskId.get(t.id);
    const fromIdx = prevSteps[i];
    let prevStep = fromId;
    if (!prevStep && fromIdx && (fromIdx.agentTaskId === t.id || !fromIdx.agentTaskId)) {
      prevStep = fromIdx;
    }
    const description = t.visibleSteps?.length
      ? t.visibleSteps[t.visibleSteps.length - 1]
      : undefined;
    return {
      title:        t.label,
      status:       TASK_STATUS_MAP[t.status] ?? 'pending',
      description,
      errorMessage: t.error ?? undefined,
      retryable:    t.retryable,
      skippable:    t.skippable,
      startedAt:    t.startedAt ? Date.parse(t.startedAt) : undefined,
      agentTaskId:  t.id,
      editBatch:    prevStep?.editBatch,
    };
  });
}

/** True when `applyCotSnapshot` should run (avoid infinite sync loops vs plain object identity churn). */
export function v2CotStepsDiffer(prev: CoTStepData[], next: CoTStepData[]): boolean {
  if (prev.length !== next.length) return true;
  for (let i = 0; i < next.length; i++) {
    const a = prev[i];
    const b = next[i];
    if (a.title !== b.title || a.status !== b.status || a.description !== b.description) return true;
    if ((a.errorMessage ?? '') !== (b.errorMessage ?? '')) return true;
    if (a.agentTaskId !== b.agentTaskId) return true;
    if ((a.editBatch?.id ?? '') !== (b.editBatch?.id ?? '')) return true;
    const aEd = a.editBatch?.edits.map((e) => `${e.id}:${e.status}`).join('|') ?? '';
    const bEd = b.editBatch?.edits.map((e) => `${e.id}:${e.status}`).join('|') ?? '';
    if (aEd !== bEd) return true;
  }
  return false;
}

export function cotStatusFromV2Snap(snap: AgentRunSnapshot, mergedSteps: CoTStepData[]): V2CotStatus {
  if (snap.status === 'completed' || snap.status === 'partial' || snap.status === 'cancelled') return 'done';
  if (snap.status === 'failed') return 'error';
  if (mergedSteps.length === 0) return 'planning';
  return 'working';
}
